import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import api from '@helpers/api';
import { t } from '@i18n';
import { useAuth } from '@contexts/AuthContext';
import { useCommunity } from 'contexts/community';
import { DATE_INTERVAL_PRESET_MAP, toApiDate } from '@pages/Insights/enums/dateIntervalPresets';

import { pickHelpArticles } from '../enums/helpArticles';
import { ALL_CATEGORIES, collectCategories, hasCategory } from '../enums/communityCategories';

// localStorage key holding the id of the owner community whose overview is
// open. Shared with nothing else — the Insights page keeps its own selection
// in the URL, on purpose (a shareable report link ≠ a "home" preference).
export const ACTIVE_OWNER_COMMUNITY_KEY = 'home:active-owner-community';

// How many rows the "recent" list card asks for.
const RECENT_LIMIT = 10;

// Months of history the growth card plots (current month included).
const GROWTH_MONTHS = 6;

// Month keys, not labels: the short name is resolved at call time so it follows
// the active language.
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function monthLabel(index) {
    return t(`dashboard:months.short.${MONTH_KEYS[index]}`);
}

function readStoredCommunityId() {
    try {
        return localStorage.getItem(ACTIVE_OWNER_COMMUNITY_KEY);
    } catch (e) {
        return null;
    }
}

function storeCommunityId(id) {
    try {
        localStorage.setItem(ACTIVE_OWNER_COMMUNITY_KEY, String(id));
    } catch (e) {
        // Storage can be unavailable (private mode / quota) — the tab still works,
        // it just won't be remembered.
    }
}

// The api helper resolves with the axios *error* on failure instead of throwing,
// so a successful call is one that carries a 2xx `status` and a body.
function okData(response) {
    if (!response || response.isAxiosError) return null;
    if (typeof response.status === 'number' && (response.status < 200 || response.status >= 300)) return null;
    return response.data ?? null;
}

// Resource collections may come bare (withoutWrapping) or under `data`.
function asList(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
}

// The sales report buckets everything per currency: BRL when it exists (the
// platform's national default), else the first currency with charges.
function pickCurrency(byCurrency) {
    const keys = byCurrency && typeof byCurrency === 'object' ? Object.keys(byCurrency) : [];
    return keys.includes('BRL') ? 'BRL' : keys[0] || 'BRL';
}

function normalizeSalesReport(raw) {
    const currency = pickCurrency(raw?.summary?.byCurrency);
    const summary = raw?.summary?.byCurrency?.[currency] || null;
    const trend = (raw?.salesAmount?.byCurrency?.[currency]?.data || []).map(p => Number(p?.value) || 0);

    return {
        currency,
        salesTotal: Number(summary?.salesTotal) || 0,
        revenue: {
            total: Number(summary?.revenue?.total) || 0,
            liquid: Number(summary?.revenue?.liquid) || 0,
            commission: Number(summary?.revenue?.commission) || 0,
            averageTicket: Number(summary?.revenue?.averageTicket) || 0,
        },
        trend,
    };
}

// Month-over-month revenue: one bucket per month, the last being the current
// (partial) month. `growth` compares the last two buckets; null when the
// previous month had no revenue (a percentage of zero is meaningless).
function normalizeGrowthReport(raw) {
    const currency = pickCurrency(raw?.revenue?.byCurrency);
    const points = raw?.revenue?.byCurrency?.[currency]?.data || [];

    const months = points.map(p => {
        const from = p?.date?.from ? new Date(String(p.date.from).replace(' ', 'T')) : null;
        return {
            label: from && !Number.isNaN(from.getTime()) ? monthLabel(from.getMonth()) : '',
            value: Number(p?.value) || 0,
        };
    });

    const current = months[months.length - 1]?.value ?? 0;
    const previous = months[months.length - 2]?.value ?? 0;
    const growth = previous > 0 ? Math.round(((current - previous) / previous) * 100) : null;

    return { currency, months, current, previous, growth };
}

async function fetchSalesReport(communityId) {
    const { from, to } = DATE_INTERVAL_PRESET_MAP.this_month.resolve();
    const params = new URLSearchParams();
    params.append('date_from', toApiDate(from));
    params.append('date_to', toApiDate(to));
    params.append('granularity_type', 'day');
    params.append('granularity_amount', '1');

    const data = okData(await api.get(`community/${communityId}/insights/salesReport?${params.toString()}`));
    return data ? normalizeSalesReport(data) : null;
}

async function fetchGrowthReport(communityId) {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - (GROWTH_MONTHS - 1), 1, 0, 0, 0, 0);
    const to = new Date(now);
    to.setHours(23, 59, 59, 0);

    const params = new URLSearchParams();
    params.append('date_from', toApiDate(from));
    params.append('date_to', toApiDate(to));
    params.append('granularity_type', 'month');
    params.append('granularity_amount', '1');

    const data = okData(await api.get(`community/${communityId}/insights/salesReport?${params.toString()}`));
    return data ? normalizeGrowthReport(data) : null;
}

async function fetchRecentSubscriptions(communityId) {
    const params = new URLSearchParams();
    params.append('community', String(communityId));
    params.append('page', '1');
    params.append('per_page', String(RECENT_LIMIT));

    const data = okData(await api.get(`subscription/search?${params.toString()}`));
    if (!data) return null;

    return {
        items: Array.isArray(data.data) ? data.data : [],
        records: Number(data.records) || 0,
        activeRecords: Number(data.summary?.active?.records) || 0,
    };
}

async function fetchPlansCount(communityId) {
    const data = okData(await api.get(`community/${communityId}/plan`));
    return data ? asList(data).length : null;
}

// Groups + content volume, as the hero counters and participation tiles read
// them. `community/list` ships this as `summary` (batched server-side); null
// when the entry predates that field, so the caller falls back to fetching.
function statsFromSummary(community) {
    const summary = community?.summary;
    if (!summary || typeof summary !== 'object') return null;

    return {
        groups: Number(summary.groups?.total) || 0,
        contentMinutes: Number(summary.contents?.minutes) || 0,
        contentsAmount: Number(summary.contents?.amount) || 0,
    };
}

// Fallback for a `community/list` payload without `summary`: the same three
// numbers assembled from the per-community group + sections endpoints.
async function fetchCommunityStats(communityId) {
    const [groupsRes, sectionsRes] = await Promise.all([
        api.get(`community/${communityId}/group`).catch(() => null),
        api.get(`community/${communityId}/sections`).catch(() => null),
    ]);

    const groups = okData(groupsRes);
    const sections = okData(sectionsRes);

    const sectionList = asList(sections);
    const contentMinutes = sectionList.reduce((sum, s) => sum + (Number(s?.summary?.contents?.minutes) || 0), 0);
    const contentsAmount = sectionList.reduce((sum, s) => sum + (Number(s?.summary?.contents?.amount) || 0), 0);

    return {
        groups: groups ? (Number(groups?.meta?.total) || asList(groups).length) : 0,
        contentMinutes,
        contentsAmount,
    };
}

export default function useHome() {
    const { user } = useAuth();
    const { communities, loading: communitiesLoading, community: communityActions } = useCommunity();

    const allCommunities = useMemo(() => communities || [], [communities]);

    // The owner-only cards (§ "first cards") key off `role === 'owner'` — creators
    // (co-admins) see the community in the list below but not its overview.
    const ownerCommunities = useMemo(
        () => allCommunities.filter(c => c.role === 'owner'),
        [allCommunities]
    );
    const isOwner = ownerCommunities.length > 0;

    // ── Active owner tab (persisted) ────────────────────────────────────────
    // Resolution order: stored id when it still belongs to an owned community →
    // otherwise the first owned community. Storage is only written on an explicit
    // tab click, so a fallback never overwrites a preference that may become
    // valid again (e.g. a community temporarily missing from the list).
    const [activeCommunityId, setActiveCommunityId] = useState(null);

    useEffect(() => {
        if (!ownerCommunities.length) {
            setActiveCommunityId(null);
            return;
        }

        const isOwned = id => ownerCommunities.some(c => String(c.id) === String(id));

        if (activeCommunityId && isOwned(activeCommunityId)) return;

        const stored = readStoredCommunityId();
        const next = stored && isOwned(stored)
            ? ownerCommunities.find(c => String(c.id) === String(stored)).id
            : ownerCommunities[0].id;

        setActiveCommunityId(next);
    }, [ownerCommunities, activeCommunityId]);

    const selectCommunity = useCallback((key) => {
        const match = ownerCommunities.find(c => String(c.id) === String(key));
        if (!match) return;
        setActiveCommunityId(match.id);
        storeCommunityId(match.id);
    }, [ownerCommunities]);

    const activeCommunity = useMemo(
        () => ownerCommunities.find(c => c.id === activeCommunityId) || null,
        [ownerCommunities, activeCommunityId]
    );

    // ── Per-community owner overview ────────────────────────────────────────
    // Month-to-date report (KPIs), 6-month growth series, recent subscriptions
    // and the plan count. Cached by community id so switching tabs back and
    // forth doesn't refetch; each community loads once per page visit.
    const [overviews, setOverviews] = useState({});
    const inFlightRef = useRef(new Set());

    useEffect(() => {
        if (!activeCommunityId) return;
        if (overviews[activeCommunityId] || inFlightRef.current.has(activeCommunityId)) return;

        inFlightRef.current.add(activeCommunityId);
        setOverviews(prev => ({ ...prev, [activeCommunityId]: { loading: true } }));

        Promise.all([
            fetchSalesReport(activeCommunityId).catch(() => null),
            fetchGrowthReport(activeCommunityId).catch(() => null),
            fetchRecentSubscriptions(activeCommunityId).catch(() => null),
            fetchPlansCount(activeCommunityId).catch(() => null),
        ]).then(([report, growth, subscriptions, plansCount]) => {
            inFlightRef.current.delete(activeCommunityId);
            setOverviews(prev => ({
                ...prev,
                [activeCommunityId]: { loading: false, report, growth, subscriptions, plansCount },
            }));
        });
    }, [activeCommunityId, overviews]);

    const activeOverview = (activeCommunityId && overviews[activeCommunityId]) || { loading: true };

    // ── Per-community stats (groups + content volume), for every community ──
    // Shown on the participation tiles and the owner hero counters. Read straight
    // off the list's `summary`; only a community whose entry lacks it triggers
    // the per-community fallback fetch, once per id. Missing entry ⇒ loading.
    const [fetchedStats, setFetchedStats] = useState({});
    const statsInFlightRef = useRef(new Set());

    useEffect(() => {
        allCommunities.forEach(c => {
            if (statsFromSummary(c)) return;
            if (fetchedStats[c.id] || statsInFlightRef.current.has(c.id)) return;
            statsInFlightRef.current.add(c.id);

            fetchCommunityStats(c.id)
                .catch(() => ({ groups: 0, contentMinutes: 0, contentsAmount: 0 }))
                .then(stats => {
                    statsInFlightRef.current.delete(c.id);
                    setFetchedStats(prev => ({ ...prev, [c.id]: stats }));
                });
        });
    }, [allCommunities, fetchedStats]);

    const communityStats = useMemo(() => {
        const stats = {};
        allCommunities.forEach(c => {
            const entry = statsFromSummary(c) || fetchedStats[c.id];
            if (entry) stats[c.id] = entry;
        });
        return stats;
    }, [allCommunities, fetchedStats]);

    // ── Participation list: text search + category filter ───────────────────
    // The search field is collapsed behind the title's icon button; closing it
    // clears the term so the grid is never filtered by a query nobody can see.
    const [communitySearchOpen, setCommunitySearchOpen] = useState(false);
    const [communitySearch, setCommunitySearch] = useState('');
    const [communityCategory, setCommunityCategory] = useState(ALL_CATEGORIES);

    const toggleCommunitySearch = useCallback(() => {
        setCommunitySearchOpen(open => {
            if (open) setCommunitySearch('');
            return !open;
        });
    }, []);

    // Blur collapses the field back into its icon — but only while it's empty.
    // Collapsing with a term typed would drop the filter the moment the user
    // reaches for a result, so a field with text stays open until it's cleared
    // or dismissed with Escape / the chip.
    const closeCommunitySearchIfEmpty = useCallback(() => {
        setCommunitySearch(term => {
            if (!term.trim()) setCommunitySearchOpen(false);
            return term;
        });
    }, []);

    const communityCategories = useMemo(() => collectCategories(allCommunities), [allCommunities]);

    // A category can disappear between loads (its last content was recategorised).
    // Fall back to "Todas" rather than leaving a chip selected that matches nothing.
    useEffect(() => {
        if (communityCategory === ALL_CATEGORIES) return;
        if (!communityCategories.includes(communityCategory)) setCommunityCategory(ALL_CATEGORIES);
    }, [communityCategories, communityCategory]);

    const filteredCommunities = useMemo(() => {
        const term = communitySearch.trim().toLowerCase();

        return allCommunities.filter(c => {
            if (!hasCategory(c, communityCategory)) return false;
            if (!term) return true;
            return `${c.name || ''} ${c.description || ''}`.toLowerCase().includes(term);
        });
    }, [allCommunities, communitySearch, communityCategory]);

    const communitiesFiltered = communityCategory !== ALL_CATEGORIES || communitySearch.trim().length > 0;

    const clearCommunityFilters = useCallback(() => {
        setCommunityCategory(ALL_CATEGORIES);
        setCommunitySearch('');
        setCommunitySearchOpen(false);
    }, []);

    // ── Help articles — creator-oriented for owners, member-oriented otherwise ─
    const helpArticles = useMemo(() => pickHelpArticles(isOwner ? 'owner' : 'member'), [isOwner]);

    // ── Navigation helpers used by the cards ─────────────────────────────────
    const openCommunity = useCallback((c) => communityActions.open(c.slug), [communityActions]);
    const openSettings = useCallback((c) => communityActions.management.open(c), [communityActions]);
    const shareCommunity = useCallback((c) => communityActions.triggerShareModal(c), [communityActions]);

    return {
        user,

        communitiesLoading,
        allCommunities,
        ownerCommunities,
        isOwner,

        activeCommunity,
        activeCommunityId,
        selectCommunity,
        activeOverview,

        communityStats,
        helpArticles,

        filteredCommunities,
        communitiesFiltered,
        communityCategories,
        communityCategory,
        setCommunityCategory,
        communitySearch,
        setCommunitySearch,
        communitySearchOpen,
        toggleCommunitySearch,
        closeCommunitySearchIfEmpty,
        clearCommunityFilters,

        openCommunity,
        openSettings,
        shareCommunity,
    };
}
